import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AuthGuard } from '@/features/security';
import { MainLayout } from '@/features/layout';
import BoundaryDeRuta from './BoundaryDeRuta';
import { RouteConfig } from './types';
import { tituloDeRuta } from './tituloDeRuta';

/**
 * El envoltorio que App.tsx aplica a CADA ruta: título por defecto, AuthGuard y
 * MainLayout. Vive acá (y no inline en App.tsx) para que el test ejercite el mismo
 * código que corre en prod. (Había un `RouteRenderer` que la app nunca usó; se borró en GH#2009.)
 *
 * Título por defecto (WCAG 2.4.2): medido en prod el 29-sep, las páginas sin
 * <Helmet> propio CONSERVABAN el título de la pantalla anterior al navegar dentro
 * de la app (Leads decía «Documentos y Contratos»; Configuración, «Publicaciones y
 * Redes»). Se monta ANTES que la página: en react-helmet-async gana el <Helmet>
 * más profundo, así que toda página que ya pone su título lo sigue poniendo.
 */
/**
 * Nombre legible del módulo para la RouteCrashCard cuando la ruta no declara
 * `title` («/agencia/crm/leads» → «Agencia Crm Leads»). Venía de RouteRenderer.
 */
export function deriveModuleName(path: string): string {
  if (!path || path === '/') return 'Inicio';
  const segments = path
    .split('/')
    .filter(Boolean)
    // Drop dynamic segments (:slug, :id, *) — no aportan al label
    .filter((seg) => !seg.startsWith(':') && seg !== '*');
  if (segments.length === 0) return 'Inicio';
  return segments
    .map((seg) => seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(' ');
}

export const envolverRuta = (route: RouteConfig): React.ReactElement => {
  const { path, element, roles, requiresAuth, requiresLayout, title } = route;

  // GH#2009 — RouteErrorBoundary vivía sólo en RouteRenderer, que la app no usa:
  // en prod el crash de UNA página tiraba la app entera al AppErrorBoundary global
  // (sin menú) y el reporte a la caja negra llegaba sin routePath. Va por DENTRO de
  // MainLayout para que el layout sobreviva. El <Helmet> queda FUERA del boundary:
  // si la página lanza, la pestaña sigue diciendo dónde estás.
  let wrappedElement: React.ReactElement = (
    <>
      <Helmet>
        <title>{tituloDeRuta(title)}</title>
      </Helmet>
      <BoundaryDeRuta moduleName={title ?? deriveModuleName(path)} routePath={path}>
        {element}
      </BoundaryDeRuta>
    </>
  );

  // Wrap with AuthGuard if authentication is required
  if (requiresAuth) {
    wrappedElement = <AuthGuard roles={roles}>{wrappedElement}</AuthGuard>;
  }

  // Wrap with MainLayout if layout is required
  if (requiresLayout) {
    wrappedElement = <MainLayout>{wrappedElement}</MainLayout>;
  }

  return wrappedElement;
};
